/**
 * Upgrade prompts for plan-gated features (pricing v2).
 *
 * showModal() checks MODAL_FEATURES: opening a window the plan does not
 * include shows the upgrade prompt instead. Upgrades happen in the Stripe
 * customer portal.
 */

import Swal from "sweetalert2";
import * as m from "@app/paraglide/messages.js";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { can, canUseOfflineDesktop, isFreePlan, isTrial, shouldWatermark } from "@app/lib/entitlements.js";
import { environment } from "@app/store/envStore.svelte.js";

export const BILLING_PORTAL_URL = "https://billing.stripe.com/p/login/eVqaEX0YyczidOadSsfQI00";

/** Modal name -> feature it needs. Modals not listed are open to every plan. */
export const MODAL_FEATURES = {
    automaticTranscription: "aiTranscription",
    batchTranscription: "batch",
    automaticTranslation: "translation",
    automaticSceneDescriptions: "shotDescriptions",
    aiSceneDescriptionImport: "shotDescriptions",
    embedCaptionsExport: "embed608",
    subtitleExtract: "extract608",
    iconikImport: "iconik",
    iconikGetProxy: "iconik",
    iconikPublish: "iconik",
    iconikMetadata: "iconik",
    workOrderImport: "workOrderApi",
    workOrderPublish: "workOrderApi",
    teamManager: "teams",
    // Free exports SRT, VTT and TXT from Subtitle Export only; every other export needs a paid plan or trial.
    videoExport: "broadcastExport",
    transcriptExport: "broadcastExport",
    dialogueListExport: "broadcastExport",
    imageExport: "broadcastExport",
    ccslExport: "broadcastExport",
    batchSubtitleExport: "broadcastExport",
    audioDescriptionExport: "broadcastExport",
    audioDubbingExport: "broadcastExport",
    audioEmbedExport: "broadcastExport"
};

/** Whether a modal may open; online-only and desktop-only checks stay where they are. */
export function modalAllowed(modalName) {
    // Offline and iframe builds have their own licensing.
    if (!environment.online || environment.iframe) return true;
    if (modalName === "offlineActivation") return canUseOfflineDesktop();
    const feature = MODAL_FEATURES[modalName];
    return !feature || can(feature);
}

/** The upgrade prompt text for a modal the plan does not include. */
export function modalUpgradeText(modalName) {
    if (modalName === "offlineActivation") {
        return isFreePlan() ? m.upgrade_required_free_text() : m.upgrade_required_text();
    }
    return MODAL_FEATURES[modalName] === "broadcastExport" && isFreePlan() ? m.upgrade_export_free_text() : undefined;
}

export function openExternal(url) {
    if (environment.electron && window.ipcRenderer) {
        window.ipcRenderer.invoke("openExternal", url);
    } else {
        window.open(url, "_blank", "noopener,noreferrer");
    }
}

/**
 * Tell the user the feature needs a higher plan, with a way to upgrade.
 * @param {string} [text]  a more specific explanation than the default
 */
export async function showUpgradePrompt(text) {
    const result = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.trial,
        titleText: m.upgrade_required_title(),
        text: text || (isFreePlan() ? m.upgrade_required_free_text() : m.upgrade_required_text()),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.upgrade_view_plans(),
        denyButtonText: m.upgrade_not_now(),
    });
    if (result.isConfirmed) {
        openExternal(BILLING_PORTAL_URL);
    }
}

/**
 * Warn before an export that will carry the "TRIAL VERSION" text (trials only:
 * Free can only reach the exports that are never watermarked).
 * @param {string} kind  see entitlements.shouldWatermark
 * @param {Function} [trialWarning]  the export window's trial warning
 */
export function warnIfWatermarked(kind, trialWarning) {
    if (shouldWatermark(kind) && isTrial() && trialWarning) trialWarning();
}

const LAST_PLAN_KEY = "cc-last-plan";

/**
 * After sign-in: tell a user once that their trial ended and they are on Free.
 * Remembers the last plan seen on this device to spot the change.
 */
export function notifyPlanChange(ent) {
    if (!ent || !ent.tier) return;
    let last = null;
    try {
        last = JSON.parse(localStorage.getItem(LAST_PLAN_KEY) || "null");
        localStorage.setItem(LAST_PLAN_KEY, JSON.stringify({ tier: ent.tier, trial: Boolean(ent.trial) }));
    } catch (err) {
        return;
    }
    if (!last || !last.trial || last.tier === "free" || ent.tier !== "free") return;

    Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.trial,
        titleText: m.free_plan_notice_title(),
        text: m.free_plan_notice_text(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.upgrade_view_plans(),
        denyButtonText: m.common_continue(),
    }).then((result) => {
        if (result.isConfirmed) openExternal(BILLING_PORTAL_URL);
    });
}
